import { useState } from "react";
import { Calendar, Clock, User, Phone, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackMeta } from "../lib/metaPixel";

/** In-line "Schedule a Visit" form on PropertyDetail.
 *  Posts a normal /inquiries record (marked with kind='site_visit') and also opens a WhatsApp deep-link
 *  so buyer + owner have an instant chat trail. */

function nextWeekdays(count = 7) {
    const out = [];
    const today = new Date();
    for (let i = 1; i <= count; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        out.push(d);
    }
    return out;
}

const SLOTS = ["10:00 AM", "12:00 PM", "3:00 PM", "5:00 PM"];

export default function ScheduleVisitForm({ property, ownerPhone }) {
    const days = nextWeekdays(7);
    const [selectedDate, setSelectedDate] = useState(days[0].toISOString().slice(0, 10));
    const [slot, setSlot] = useState(SLOTS[0]);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name || !/^\d{10}$/.test(phone.replace(/\D/g, "").slice(-10))) {
            toast.error("Please enter your name and a valid 10-digit phone number.");
            return;
        }
        setSubmitting(true);
        const readableDate = new Date(selectedDate).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" });
        const message = `Site-visit request for "${property.title}"\nBuyer: ${name} · ${phone}\nDate: ${readableDate} · Slot: ${slot}\nLink: ${window.location.origin}/properties/${property.id}`;
        try {
            await api.post("/inquiries", {
                property_id: property.id,
                name, email: "", phone,
                message: `[SITE VISIT] ${readableDate} · ${slot} · ${property.title}`,
                kind: "site_visit",
            }).catch(() => {}); // fire-and-forget — WhatsApp handoff is the primary channel
            try {
                trackMeta("Lead",
                    { content_ids: [property.id], content_name: property.title, content_type: "property", lead_type: "site_visit", value: property.price, currency: "INR" },
                    { phone }
                );
            } catch { /* silent */ }
            setDone(true);
            toast.success("Site visit request sent. Owner will confirm shortly.");
            // Open WhatsApp with pre-filled message — only if the owner phone is available
            const target = (ownerPhone || "").replace(/\D/g, "");
            if (target) {
                const wa = `https://wa.me/${target}?text=${encodeURIComponent(message)}`;
                window.open(wa, "_blank", "noopener");
            }
        } catch {
            toast.error("Could not send request. Please email globalpropertymart95@gmail.com.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" data-testid="schedule-visit-done">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <div className="font-heading font-bold text-navy-500 text-lg">Visit request sent!</div>
                <div className="text-navy-500/70 text-sm mt-1">Owner has been notified. Check your WhatsApp for updates.</div>
                <button
                    onClick={() => setDone(false)}
                    className="mt-4 text-xs text-gold-700 font-bold hover:underline"
                    data-testid="schedule-visit-again"
                >Schedule another slot →</button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="rounded-2xl border border-gold-500/30 bg-white p-5 sm:p-6 space-y-4" data-testid="schedule-visit-form">
            <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gold-600" />
                <h3 className="font-heading font-bold text-navy-500 text-lg">Schedule a Site Visit</h3>
            </div>
            <p className="text-xs text-navy-500/65 -mt-2">Pick a date, slot & we&apos;ll notify the owner instantly on WhatsApp.</p>

            {/* Day chips */}
            <div className="flex gap-2 overflow-x-auto pb-1 mobile-listing" data-testid="schedule-visit-days">
                {days.map((d) => {
                    const iso = d.toISOString().slice(0, 10);
                    const active = iso === selectedDate;
                    return (
                        <button
                            key={iso}
                            type="button"
                            onClick={() => setSelectedDate(iso)}
                            data-testid={`schedule-day-${iso}`}
                            className={`shrink-0 flex flex-col items-center px-3 py-2 rounded-xl border-2 min-w-[64px] transition-colors ${
                                active ? "border-gold-500 bg-gold-500/10 text-navy-500" : "border-navy-500/10 text-navy-500/70 hover:border-gold-500/40"
                            }`}
                        >
                            <span className="text-[10px] font-semibold uppercase tracking-wider">{d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                            <span className="text-lg font-heading font-bold leading-none mt-1">{d.getDate()}</span>
                            <span className="text-[9px] uppercase mt-0.5">{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                        </button>
                    );
                })}
            </div>

            {/* Slots */}
            <div>
                <div className="text-xs font-bold text-navy-500/70 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Time slot
                </div>
                <div className="grid grid-cols-4 gap-2">
                    {SLOTS.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setSlot(s)}
                            data-testid={`schedule-slot-${s.replace(/\s+/g, "").toLowerCase()}`}
                            className={`px-2 py-2 rounded-md text-[11px] font-bold border transition-colors ${
                                slot === s ? "border-gold-500 bg-gold-500/10 text-navy-500" : "border-navy-500/15 text-navy-500/70 hover:border-gold-500/40"
                            }`}
                        >{s}</button>
                    ))}
                </div>
            </div>

            {/* Name + phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block relative">
                    <User className="w-4 h-4 text-navy-500/40 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        required
                        data-testid="schedule-visit-name"
                        className="w-full pl-9 pr-3 py-2.5 rounded-md border border-navy-500/15 text-sm text-navy-500 focus:border-gold-500/60 focus:outline-none"
                    />
                </label>
                <label className="block relative">
                    <Phone className="w-4 h-4 text-navy-500/40 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="10-digit phone"
                        required
                        data-testid="schedule-visit-phone"
                        className="w-full pl-9 pr-3 py-2.5 rounded-md border border-navy-500/15 text-sm text-navy-500 focus:border-gold-500/60 focus:outline-none"
                    />
                </label>
            </div>

            <button
                type="submit"
                disabled={submitting}
                data-testid="schedule-visit-submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md bg-gradient-to-r from-gold-500 to-amber-500 text-navy-900 font-bold text-sm hover:brightness-110 shadow-sm disabled:opacity-60"
            >
                <MessageCircle className="w-4 h-4" />
                {submitting ? "Sending…" : "Confirm & WhatsApp Owner"}
            </button>
        </form>
    );
}
